import { useMemo } from 'react';
import { computeDiscountedPrice } from '../utils/offer';
import type { ICartItem } from '../types/storefront.types';
import type { IComboCartItem } from '../types/combo.types';

export interface CartLinePricing {
  /** Precio de lista de la linea (cantidad incluida), sin descuento. */
  grossTotal: number;
  /** Total de la linea con la oferta aplicada. */
  netTotal: number;
  /** Ahorro de la linea. 0 cuando no hay oferta activa. */
  saving: number;
  hasDiscount: boolean;
}

export interface CartPricing {
  /** Suma de precios de lista de todo el carrito. */
  subtotal: number;
  /** Suma con ofertas aplicadas. */
  discountedSubtotal: number;
  /** Ahorro por ofertas (solo productos sueltos). */
  totalSaving: number;
  /** Ahorro por promos: (regularPrice - price) × qty. Va aparte de las ofertas. */
  comboSaving: number;
  hasDiscount: boolean;
  itemCount: number;
  /** Pricing por `cartItemId`, para que cada linea no recalcule lo mismo. */
  byLine: Map<string, CartLinePricing>;
}

/** El precio de la promo ya es el neto; el bruto es su regularPrice + extras. */
export const computeComboLinePricing = (item: IComboCartItem): CartLinePricing => {
  const saving = Math.max(0, item.combo.regularPrice - item.combo.price) * item.quantity;
  return {
    grossTotal: item.totalPrice + saving,
    netTotal: item.totalPrice,
    saving,
    hasDiscount: saving > 0,
  };
};

/**
 * Calcula el pricing de una linea del carrito.
 *
 * El descuento aplica SOLO sobre el neto del producto, nunca sobre los modifiers:
 * el carrito guarda siempre precios de lista (`unitPrice`/`totalPrice` brutos) y el
 * descuento se recalcula aqui a partir de `product.activeOffer`, que es la fuente de verdad.
 */
export const computeLinePricing = (item: ICartItem): CartLinePricing => {
  const offer = item.product?.activeOffer;
  const grossTotal = item.totalPrice;

  if (!offer) {
    return { grossTotal, netTotal: grossTotal, saving: 0, hasDiscount: false };
  }

  const basePrice = item.product.price ?? item.unitPrice;
  const discountedBase = computeDiscountedPrice(basePrice, offer);
  const savingPerUnit = Math.max(0, basePrice - discountedBase);
  const saving = savingPerUnit * item.quantity;

  return {
    grossTotal,
    netTotal: grossTotal - saving,
    saving,
    hasDiscount: saving > 0,
  };
};

/**
 * Totales del carrito derivados de `cart`. Es calculo puro sobre props/estado,
 * asi que se memoiza en render en vez de sincronizarse con un efecto.
 */
export const useCartPricing = (cart: ICartItem[], comboCart: IComboCartItem[] = []): CartPricing =>
  useMemo(() => {
    const byLine = new Map<string, CartLinePricing>();
    let subtotal = 0;
    let discountedSubtotal = 0;
    let totalSaving = 0;
    let comboSaving = 0;
    let itemCount = 0;

    for (const item of comboCart) {
      const line = computeComboLinePricing(item);
      byLine.set(item.cartItemId, line);
      subtotal += line.grossTotal;
      discountedSubtotal += line.netTotal;
      comboSaving += line.saving;
      itemCount += item.quantity;
    }

    for (const item of cart) {
      const line = computeLinePricing(item);
      byLine.set(item.cartItemId, line);
      subtotal += line.grossTotal;
      discountedSubtotal += line.netTotal;
      totalSaving += line.saving;
      itemCount += item.quantity;
    }

    return {
      subtotal,
      discountedSubtotal,
      totalSaving,
      comboSaving,
      hasDiscount: totalSaving > 0 || comboSaving > 0,
      itemCount,
      byLine,
    };
  }, [cart, comboCart]);
